import { ReflectUtils } from "@dooboostore/core";
class DefaultCacheStorage {
  constructor() {
    this.storage = /* @__PURE__ */ new Map();
  }
  delete(key) {
    return this.storage.delete(key);
  }
  get(key) {
    return this.storage.get(key);
  }
  has(key) {
    return this.storage.has(key);
  }
  set(key, value) {
    this.storage.set(key, value);
  }
}
const isNoAllKey = (cacheOption) => {
  return !("key" in cacheOption) && !("deleteKey" in cacheOption) && !("setKey" in cacheOption);
};
const isKey = (cacheOption) => {
  return "key" in cacheOption && (typeof cacheOption.key === "string" || typeof cacheOption.key === "function");
};
const isChildrenKey = (cacheOption) => {
  return "key" in cacheOption && typeof cacheOption.key === "object" && cacheOption.key !== null && "key" in cacheOption.key && "childrenKey" in cacheOption.key;
};
const isDeleteKey = (cacheOption) => {
  return "deleteKey" in cacheOption && (typeof cacheOption.deleteKey === "string" || typeof cacheOption.deleteKey === "function");
};
const isDeleteChildrenKey = (cacheOption) => {
  return "deleteKey" in cacheOption && typeof cacheOption.deleteKey === "object" && cacheOption.deleteKey !== null && "key" in cacheOption.deleteKey && "childrenKey" in cacheOption.deleteKey;
};
const isSetKey = (cacheOption) => {
  return "setKey" in cacheOption && (typeof cacheOption.setKey === "string" || typeof cacheOption.setKey === "function");
};
const isSetChildrenKey = (cacheOption) => {
  return "setKey" in cacheOption && typeof cacheOption.setKey === "object" && cacheOption.setKey !== null && "key" in cacheOption.setKey && "childrenKey" in cacheOption.setKey;
};
const isUpdate = (cacheOption) => {
  return isSetKey(cacheOption) || isSetChildrenKey(cacheOption);
};
const simpleApplicationCache = /* @__PURE__ */ new WeakMap();
const getCacheSet = (simpleApplication) => {
  return simpleApplicationCache.get(simpleApplication);
};
const findCacheByKey = (simpleApplication, key) => {
  const storage = simpleApplicationCache.get(simpleApplication);
  if (storage) {
    return Array.from(storage.config.entries()).find(([k, v]) => k === key);
  }
};
const findCacheByKeyStartWith = (simpleApplication, key) => {
  const storage = simpleApplicationCache.get(simpleApplication);
  if (storage) {
    return Array.from(storage.config.entries()).filter(([k, v]) => k.startsWith(key));
  } else {
    return [];
  }
};
const deleteCacheByKey = (simpleApplication, key) => {
  const storage = simpleApplicationCache.get(simpleApplication);
  if (storage) {
    storage.storage.delete(key);
    findCacheByKeyStartWith(simpleApplication, key).forEach(([k, v]) => {
      const data = storage.config.get(k);
      if (data?.expireTimeout) {
        clearTimeout(data.expireTimeout);
      }
      storage.config.delete(k);
      storage.storage.delete(k);
    });
  }
};
const deleteCacheByKeyStartWith = (simpleApplication, key) => {
  findCacheByKeyStartWith(simpleApplication, key).forEach(([key2, data]) => deleteCacheByKey(simpleApplication, key2));
};
const CacheMetadataKey = Symbol.for("simple-boot:cache-metadata");
const cacheProcess = (data, target, propertyKey, descriptor) => {
  const originalMethod = descriptor.value;
  descriptor.value = function(...args) {
    const data2 = ReflectUtils.getMetadata(CacheMetadataKey, target, propertyKey);
    const simpleApplication = this._SimpleBoot_application;
    const simstanceManager = this._SimpleBoot_simstanceManager;
    const simOption = this._SimpleBoot_simOption;
    if (simpleApplication && simstanceManager && simOption && simOption.cache?.enable && data2) {
      const simpleApplicationStorage = simpleApplicationCache.get(simpleApplication) ?? { storage: simOption.cache?.storage ?? new DefaultCacheStorage(), config: /* @__PURE__ */ new Map() };
      simpleApplicationCache.set(simpleApplication, simpleApplicationStorage);
      let rootKey;
      if (isNoAllKey(data2)) {
        rootKey = data2.key = target.constructor.name + "." + propertyKey.toString();
      }
      if (isKey(data2)) {
        rootKey = typeof data2.key === "function" ? data2.key.apply(this, args) : data2.key;
      } else if (isChildrenKey(data2)) {
        rootKey = typeof data2.key.key === "function" ? data2.key.key.apply(this, args) : data2.key.key;
      } else if (isDeleteKey(data2)) {
        rootKey = typeof data2.deleteKey === "function" ? data2.deleteKey.apply(this, args) : data2.deleteKey;
      } else if (isDeleteChildrenKey(data2)) {
        rootKey = typeof data2.deleteKey.key === "function" ? data2.deleteKey.key.apply(this, args) : data2.deleteKey.key;
      } else if (isSetKey(data2)) {
        rootKey = typeof data2.setKey === "function" ? data2.setKey.apply(this, args) : data2.setKey;
      } else if (isSetChildrenKey(data2)) {
        rootKey = typeof data2.setKey.key === "function" ? data2.setKey.key.apply(this, args) : data2.setKey.key;
      }
      if (isUpdate(data2)) {
      } else {
        if (isKey(data2) && rootKey) {
          const findTarget = findCacheByKey(simpleApplication, rootKey);
          if (findTarget) {
            const key = findTarget[0];
            return simpleApplicationStorage.storage.get(key);
          }
        } else if (isChildrenKey(data2) && rootKey) {
          const findTargets = findCacheByKeyStartWith(simpleApplication, rootKey);
          const rdata = findTargets.filter(([k, v]) => simpleApplicationStorage.storage.has(k)).map(([k, v]) => simpleApplicationStorage.storage?.get(k));
          if (rdata.length > 0) {
            return rdata;
          }
        }
      }
      const result = originalMethod.apply(this, args);
      if (isDeleteKey(data2) && rootKey) {
        deleteCacheByKey(simpleApplication, rootKey);
      } else if (isDeleteChildrenKey(data2) && rootKey) {
        const key = typeof data2.deleteKey.childrenKey === "function" ? data2.deleteKey.childrenKey.call(this, result) : data2.deleteKey.childrenKey;
        const keys = key instanceof Array ? key : [key];
        keys.forEach((it) => deleteCacheByKey(simpleApplication, `${rootKey}${data2.deleteKey.join ?? "."}${it}`));
      } else if ((isSetKey(data2) || isKey(data2)) && rootKey) {
        const ms = data2.ms ?? simOption.cache?.ms;
        const expireTimeout = ms !== void 0 ? setTimeout(() => deleteCacheByKey(simpleApplication, rootKey), ms) : void 0;
        simpleApplicationStorage.config.set(rootKey, { data: data2, expireTimeout });
        simpleApplicationStorage.storage?.set(rootKey, result);
      } else if ((isSetChildrenKey(data2) || isChildrenKey(data2)) && rootKey) {
        const key = isSetChildrenKey(data2) ? data2.setKey : data2.key;
        const childrenKeys = typeof key.childrenKey === "function" ? key.childrenKey.call(this, result) : key.childrenKey;
        const keys = childrenKeys instanceof Array ? childrenKeys : [childrenKeys];
        keys.forEach((it) => {
          const childKey = `${rootKey}${key.join ?? "."}${it.key}`;
          const ms = it.ms ?? data2.ms ?? simOption.cache?.ms;
          const expireTimeout = ms !== void 0 ? setTimeout(() => deleteCacheByKey(simpleApplication, childKey), ms) : void 0;
          simpleApplicationStorage.config.set(childKey, { data: data2, expireTimeout });
          simpleApplicationStorage.storage?.set(childKey, it.data);
        });
      }
      return result;
    } else {
      return originalMethod.apply(this, args);
    }
  };
  ReflectUtils.defineMetadata(CacheMetadataKey, data, target, propertyKey);
};
function Cache(targetOrOption, propertyKey, descriptor) {
  if (typeof targetOrOption === "object" && propertyKey && descriptor) {
    cacheProcess({}, targetOrOption, propertyKey, descriptor);
  } else if (typeof targetOrOption === "object") {
    return (target, propertyKey2, descriptor2) => {
      const data = targetOrOption;
      return cacheProcess(data, target, propertyKey2, descriptor2);
    };
  } else {
    throw new Error("Invalid arguments for Cache decorator");
  }
}
const cache = Cache;
export {
  Cache,
  DefaultCacheStorage,
  cache,
  deleteCacheByKey,
  deleteCacheByKeyStartWith,
  findCacheByKey,
  findCacheByKeyStartWith,
  getCacheSet
};
//# sourceMappingURL=CacheDecorator.js.map
